import { ProjectCategory, PROJECT_CATEGORY_CONFIGS, ProjectCategoryConfig } from "@droid/project-v2-interface";
import { Log } from "@fanfanlo";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid2,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import FlashOnIcon from '@mui/icons-material/FlashOn';
import CodeIcon from '@mui/icons-material/Code';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

const fileLog = new Log(true, 'ProjectCategorySelectDialog');
fileLog.pause = true;
fileLog.childrenPaused = true;

// 图标映射
const categoryIcons: Record<ProjectCategory, React.ReactNode> = {
  [ProjectCategory.QuickAction]: <FlashOnIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
  [ProjectCategory.ScriptRunner]: <CodeIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
  [ProjectCategory.Workflow]: <AccountTreeIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
};

interface ProjectCategorySelectDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (category: ProjectCategory) => void;
}

/**
 * 项目类别选择对话框
 */
export const ProjectCategorySelectDialog: React.FC<ProjectCategorySelectDialogProps> = ({
  open,
  onClose,
  onSelect
}) => {
  const { t } = useTranslation("droid-project-v2-ui/ui/components/content");
  fileLog.log(`[ProjectCategorySelectDialog] 渲染, open=${open}`);

  const projectCategories: ProjectCategory[] = [
    ProjectCategory.QuickAction,
    ProjectCategory.ScriptRunner,
    ProjectCategory.Workflow
  ];

  const handleSelect = (category: ProjectCategory) => {
    fileLog.log(`[ProjectCategorySelectDialog] 选择类别: ${category}`);
    onSelect(category);
  };

  const handleClose = () => {
    fileLog.log(`[ProjectCategorySelectDialog] 关闭对话框`);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('ProjectCategorySelectDialog.ProjectCategorySelectDialog.title')}</DialogTitle>
      <DialogContent>
        <Grid2 container spacing={2} sx={{ pt: 1 }}>
          {projectCategories.map((category, index) => {
            const config: ProjectCategoryConfig = PROJECT_CATEGORY_CONFIGS[category];
            fileLog.log(`[ProjectCategorySelectDialog] 渲染类别卡片: ${category}, config=`, JSON.stringify(config));

            return (
              <Grid2 size={{ xs: 12, sm: 4 }} key={category}>
                <Card
                  sx={{
                    height: '100%',
                    '&:hover': { boxShadow: 4 }
                  }}
                  data-testid={`ProjectCategorySelectDialog__categoryCard__01JFXYZ9A1B2C3D4E5F6G7H8__${index}`}
                >
                  <CardActionArea
                    onClick={() => handleSelect(category)}
                    sx={{ height: '100%', p: 2 }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 0 }}>
                      <Box sx={{ mb: 1 }}>
                        {categoryIcons[category]}
                      </Box>
                      <Typography variant="h6" gutterBottom>
                        {t(`ProjectCategorySelectDialog.${config.labelKey}`)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(`ProjectCategorySelectDialog.${config.descriptionKey}`)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid2>
            );
          })}
        </Grid2>
      </DialogContent>
    </Dialog>
  );
};
